// Attribution + entry-mode resolver for the guideline lead-gen funnel.
//
// Two jobs, kept separate on purpose (see GUIDELINES_FUNNEL_PLAN.md):
//   1. Attribution  — WHERE the visitor came from (utm_*, fbclid, ref). Captured
//      ONCE on landing, persisted first-touch for the session, then attached to
//      pixel/GA events and the Firebase lead record. Internal links stay clean;
//      we never propagate ?src= / ?utm_ across navigation.
//   2. Entry mode   — WHO the visitor is. `member` = already-owned app user
//      (logged in OR arrived in-app via ?src=app) -> serve PDF directly, no
//      capture. `lead` = unknown (FB / search / direct) -> show the opt-in form.
//
// The channel (fb vs search) never changes the on-page experience — only the
// known-vs-unknown distinction does. utm_source is just the label we store.

import { StaticTags } from "@/static";
import { getStore } from "@/config/utils";

const STORAGE_KEY = "guideline_attribution";

const ATTRIBUTION_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "src",
  "ref",
  "fbclid",
];

const readSession = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeSession = (data) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    /* sessionStorage unavailable (private mode / SSR) — degrade silently */
  }
};

/**
 * Capture attribution from the current URL — first-touch wins.
 * Safe to call on every guideline page mount; only the first call in a session
 * actually stores anything. Returns the persisted attribution object.
 */
export const captureAttribution = () => {
  const existing = readSession();
  if (existing) return existing;

  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const captured = {};
  ATTRIBUTION_FIELDS.forEach((field) => {
    const value = params.get(field);
    if (value) captured[field] = value;
  });

  // Derive a normalized channel for reporting/audiences.
  captured.channel = deriveChannel(captured);
  captured.landing_path =
    typeof window !== "undefined" ? window.location.pathname : "";

  writeSession(captured);
  return captured;
};

/**
 * Normalized traffic channel from raw params + referrer.
 *   utm_source present        -> that source (fb, google, ...)
 *   fbclid present            -> fb (clicked an FB/IG link without utm)
 *   no params + has referrer  -> organic / referral
 *   no params, no referrer    -> direct
 */
const deriveChannel = (captured) => {
  if (captured.utm_source) return captured.utm_source.toLowerCase();
  if (captured.fbclid) return "fb";
  if (captured.src === "app") return "app";
  if (typeof document !== "undefined" && document.referrer) {
    try {
      const host = new URL(document.referrer).hostname;
      if (host && !host.includes(window.location.hostname)) {
        if (/google\./.test(host)) return "organic";
        return "referral";
      }
    } catch (e) {
      /* malformed referrer */
    }
  }
  return "direct";
};

/** The persisted first-touch attribution (captures lazily if not yet stored). */
export const getAttribution = () => readSession() || captureAttribution();

/**
 * Is this an already-owned app user?
 * True when a login token is present, OR they entered in-app via ?src=app.
 */
export const isMember = () => {
  const token = getStore(StaticTags.IS_LOGGED_IN);
  const loggedIn = !!token && token !== "null" && token !== "undefined";
  if (loggedIn) return true;

  const attribution = getAttribution();
  return attribution.src === "app";
};

/** 'member' (owned -> direct PDF) or 'lead' (unknown -> capture). */
export const getEntryMode = () => (isMember() ? "member" : "lead");